import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebaseConfig';
import { IoAdd, IoTrash, IoImage, IoClose, IoEye, IoEyeOff } from 'react-icons/io5';
import { toast } from 'react-toastify';

interface Testimonial {
  id: string;
  imageUrl: string;
  name?: string;
  isActive: boolean;
  order?: number;
  createdAt: any;
}

export const TestimonialManager: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);

  // Form states
  const [testimonialName, setTestimonialName] = useState('');
  const [testimonialOrder, setTestimonialOrder] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  // Fetch testimonials
  useEffect(() => {
    const testimonialsRef = collection(db, 'testimonials');

    const unsubscribe = onSnapshot(testimonialsRef, (snapshot) => {
      const testimonialsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Testimonial[];

      testimonialsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setTestimonials(testimonialsData);
      setLoading(false);
    }, (snapshotError) => {
      console.error('Testimonial snapshot error:', snapshotError);
      setError('Unable to load testimonials. Check Firestore permissions for the testimonials collection.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const storageRef = ref(storage, `testimonials/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setImageUrl(url);
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddTestimonial = async () => {
    if (!imageUrl) {
      toast.error('Please upload an image');
      return;
    }

    try {
      const testimonialData: any = {
        imageUrl: imageUrl,
        isActive: true,
        order: testimonialOrder ? parseInt(testimonialOrder) : testimonials.length + 1,
        createdAt: serverTimestamp(),
      };

      // Only add name if provided
      if (testimonialName.trim()) {
        testimonialData.name = testimonialName.trim();
      }

      await addDoc(collection(db, 'testimonials'), testimonialData);
      toast.success('Testimonial added successfully');

      // Reset form
      setShowAddModal(false);
      setTestimonialName('');
      setTestimonialOrder('');
      setImageUrl('');
    } catch (err) {
      console.error('Error adding testimonial:', err);
      toast.error('Failed to add testimonial');
    }
  };

  const handleEditTestimonial = async () => {
    if (!editingTestimonial || !imageUrl) {
      toast.error('Please provide image');
      return;
    }

    try {
      const testimonialRef = doc(db, 'testimonials', editingTestimonial.id);
      const updateData: any = {
        imageUrl: imageUrl,
        order: testimonialOrder ? parseInt(testimonialOrder) : editingTestimonial.order,
      };

      // Only add name if provided
      if (testimonialName.trim()) {
        updateData.name = testimonialName.trim();
      }

      await updateDoc(testimonialRef, updateData);

      toast.success('Testimonial updated successfully');

      // Reset form
      setShowEditModal(false);
      setEditingTestimonial(null);
      setTestimonialName('');
      setTestimonialOrder('');
      setImageUrl('');
    } catch (err) {
      console.error('Error updating testimonial:', err);
      toast.error('Failed to update testimonial');
    }
  };

  const handleDeleteTestimonial = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await deleteDoc(doc(db, 'testimonials', id));
      toast.success('Testimonial deleted successfully');
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      toast.error('Failed to delete testimonial');
    }
  };

  const toggleActive = async (testimonial: Testimonial) => {
    try {
      const testimonialRef = doc(db, 'testimonials', testimonial.id);
      await updateDoc(testimonialRef, {
        isActive: !testimonial.isActive,
      });
      toast.success(`Testimonial ${!testimonial.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      console.error('Error toggling testimonial:', err);
      toast.error('Failed to toggle testimonial');
    }
  };

  const resetAddForm = () => {
    setTestimonialName('');
    setTestimonialOrder('');
    setImageUrl('');
  };

  const closeAddModal = () => {
    resetAddForm();
    setShowAddModal(false);
  };

  const resetEditForm = () => {
    setEditingTestimonial(null);
    setTestimonialName('');
    setTestimonialOrder('');
    setImageUrl('');
  };

  const closeEditModal = () => {
    resetEditForm();
    setShowEditModal(false);
  };

  const openEditModal = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialName(testimonial.name || '');
    setTestimonialOrder(testimonial.order?.toString() || '');
    setImageUrl(testimonial.imageUrl);
    setShowEditModal(true);
  };

  if (loading) {
    return <div className="text-center py-8">Loading testimonials...</div>;
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Testimonial Manager</h2>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
        >
          <IoAdd size={20} />
          Add Testimonial
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="border border-gray-200 rounded-lg p-4">
            <div className="aspect-square mb-4 overflow-hidden rounded-lg">
              <img
                src={testimonial.imageUrl}
                alt={testimonial.name || 'Testimonial'}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900">{testimonial.name || 'Unnamed'}</p>
                <p className="text-sm text-gray-500">Order: {testimonial.order || 'N/A'}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(testimonial)}
                  className={`p-2 rounded-lg ${testimonial.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                  title={testimonial.isActive ? 'Deactivate' : 'Activate'}
                >
                  {testimonial.isActive ? <IoEye size={20} /> : <IoEyeOff size={20} />}
                </button>
                <button
                  onClick={() => openEditModal(testimonial)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Edit"
                >
                  <IoImage size={20} />
                </button>
                <button
                  onClick={() => handleDeleteTestimonial(testimonial.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <IoTrash size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add Testimonial</h3>
              <button onClick={closeAddModal} className="text-gray-500 hover:text-gray-700">
                <IoClose size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (Optional)</label>
                <input
                  type="text"
                  value={testimonialName}
                  onChange={(e) => setTestimonialName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order (Optional)</label>
                <input
                  type="number"
                  value={testimonialOrder}
                  onChange={(e) => setTestimonialOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Display order"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full"
                  disabled={uploadingImage}
                />
                {uploadingImage && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                {imageUrl && (
                  <div className="mt-2">
                    <img src={imageUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddTestimonial}
                className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 transition-colors"
                disabled={!imageUrl || uploadingImage}
              >
                Add Testimonial
              </button>
              <button
                onClick={closeAddModal}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingTestimonial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Testimonial</h3>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700">
                <IoClose size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (Optional)</label>
                <input
                  type="text"
                  value={testimonialName}
                  onChange={(e) => setTestimonialName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order (Optional)</label>
                <input
                  type="number"
                  value={testimonialOrder}
                  onChange={(e) => setTestimonialOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Display order"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full"
                  disabled={uploadingImage}
                />
                {uploadingImage && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                {imageUrl && (
                  <div className="mt-2">
                    <img src={imageUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleEditTestimonial}
                className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 transition-colors"
                disabled={!imageUrl || uploadingImage}
              >
                Update Testimonial
              </button>
              <button
                onClick={closeEditModal}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};